import 'bootstrap';
import * as d3 from 'd3';
import Tooltip from './tooltip';

const radius = 4;
const transitionTime = 750;
const tooltip = new Tooltip();

class ClusterView {
	constructor(container) {

		this.container = container;

		// append the svg object to the body of the page
		this.svg = d3.select(container)
			.append("svg")
			.attr("width", container.clientWidth)
			.attr("height", container.clientHeight);


		// Create two sections, one with the graph itself and one with the legend

		// The client container should be 4/3 aspect ratio, use height for both
		const graphWidth = container.clientHeight;
		const graphHeight = container.clientHeight;

		// set the dimensions and margins of the graph
		var margin = { top: 10, right: 10, bottom: 30, left: 30 },
			width = graphWidth - margin.left - margin.right,
			height = graphHeight - margin.top - margin.bottom;

		this.margin = margin;
		this.width = width;
		this.height = height;

		this.g = this.svg.append("g")
			.attr("transform", `translate(${this.margin.left},${this.margin.top})`);

		this.gX = this.g.append("g")
			.attr("class", "axis axis--x")
			.attr("transform", `translate(0,${this.height})`);

		this.gY = this.g.append("g")
			.attr("class", "axis axis--y");

		this.defs = this.svg.append('defs');

		this.defs
			.append("svg:clipPath")
			.attr("id", "clip")
			.append("svg:rect")
			.attr("x", 0)
			.attr("y", 0)
			.attr("width", this.width)
			.attr("height", this.height);

		this.plot = this.g.append("g")
			.attr("class", "plot")
			.attr("width", this.width)
			.attr("height", this.height)
			.attr("clip-path", "url(#clip)");

		this.plotData = this.plot.append('g')
			.attr("width", this.width)
			.attr("height", this.height);

		// Set up listener
		[...document.querySelectorAll(".by-btn")].forEach(btn => {
			btn.addEventListener("change", (e) => this.changeClusterBy(e.target.dataset.by));

			if (btn.checked)
				this.changeClusterBy(btn.dataset.by);
		})

		this.legend = this.svg.append("g")
			.attr("transform", `translate(${this.width + 2 * this.margin.left},${this.margin.top})`);

		this.legendWidth = container.clientWidth - container.clientHeight;

		this.data = null;
		this.loadData();
	}

	changeClusterBy(by) {
		this.clusterBy = by;
		if (typeof this.clusters === "object") {
			const data = this.clusters.get(by).filter(d => d.cluster !== null && d.x !== null && d.y !== null);

			const clusters_data = d3.group(data, d => d.cluster);
			const clusters = [...clusters_data.keys()].sort();
			const color = d3.scaleOrdinal(clusters, d3.schemeTableau10);

			const x = d3.scaleLinear()
				.domain(d3.extent(data, d => d.x))
				.range([radius, this.width - 2 * radius]);

			const y = d3.scaleLinear()
				.domain(d3.extent(data, d => d.y))
				.range([this.height - 2 * radius, radius]);

			const xAxis = d3.axisBottom(x).tickSizeOuter(3);
			const yAxis = d3.axisLeft(y).tickSizeOuter(3);

			this.gX.transition().duration(transitionTime).call(xAxis);
			this.gY.transition().duration(transitionTime).call(yAxis);

			const t = this.svg.transition().duration(transitionTime);

			this.plotData.selectAll(".complex")
				.data(data)
				.join(
					enter => enter.append("circle")
						.attr("class", "complex")
						.attr("fill", d => color(d.cluster))
						.attr("cx", d => x(d.x))
						.attr("cy", d => y(d.y))
						.attr("r", radius)
						.on("mouseover", (e, d) => tooltip.show(`complex: ${d.id}\ncluster: ${d.cluster_name}`, e.pageX + radius, e.pageY + radius))
						.on("mouseout", () => tooltip.hide())
						.on("click", (e, d) => window.location.href = `complex.html?id=${d.id}`),
					update => update.call(update => update.transition(t)
						.attr("fill", d => color(d.cluster))
						.attr("cx", d => x(d.x))
						.attr("cy", d => y(d.y))),
					exit => exit.remove()
				);

			// Update legend

			const legendH = 20, legendW = this.legendWidth;

			const l = this.legend.selectAll(".legend")
				.data(clusters, d => d)
				.join(
					enter => {
						const l2 = enter.append("g")
							.attr("class", "legend")
							.attr("width", legendW)
							.attr("height", legendH)
							.on("mouseover", (e, d) => this.hoverCluster(d))
							.on("mouseout", (e, d) => this.removeHoverCluster(d));

						l2.append("circle")
							.attr("cx", 2 * radius)
							.attr("cy", (legendH - radius) / 2)
							.attr("r", radius);

						l2.append("text")
							.attr("x", 5 * radius)
							.attr("y", legendH - radius);
					},
					update => update,
					exit => exit.remove()
				);

			this.legend.selectAll(".legend")
				.attr("transform", d => `translate(0, ${legendH * clusters.indexOf(d)})`);

			this.legend.selectAll(".legend circle")
				.attr("fill", d => color(d));

			this.legend.selectAll(".legend text")
				.text(d => clusters_data.get(d)[0].cluster_name)
		}
	}

	async loadData() {
		try {
			const r = await fetch("data.json");
			if (r.ok == false)
				throw r.statusText;
			const data = await r.json();

			const GO_clusters = [];
			const OT_clusters = [];

			for (let complex of data) {

				if (complex["Coordinates_UMAP_GOxSTRING"].length === 2) {
					GO_clusters.push({
						"id": complex.id,
						"cluster": complex["Cluster_GOxSTRING"],
						"cluster_name": complex["Cluster_GOxSTRING_Name"],
						"x": +complex["Coordinates_UMAP_GOxSTRING"][0],
						"y": +complex["Coordinates_UMAP_GOxSTRING"][1]
					})
				}

				if (complex["Coordinates_UMAP_OT"].length === 2) {
					OT_clusters.push({
						"id": complex.id,
						"cluster": complex["Cluster_OT"],
						"cluster_name": complex["Cluster_OT_Name"],
						"x": +complex["Coordinates_UMAP_OT"][0],
						"y": +complex["Coordinates_UMAP_OT"][1]
					})
				}

			}

			this.clusters = new Map();
			this.clusters.set("GO", GO_clusters);
			this.clusters.set("OT", OT_clusters);

			this.changeClusterBy(this.clusterBy);

			this.container.parentNode.classList.add("has-data");
		} catch (error) {
			const alert = document.getElementById("load-status");
			alert.classList.remove("alert-primary");
			alert.classList.add("alert-warning");
			alert.textContent = `Error loading data: ${error}`;
		}
	}

	hoverCluster(cluster) {
		this.plotData.selectAll(".complex")
			.filter((d, i) => d.cluster !== cluster)
			.style("opacity", 0.25);
		this.legend.selectAll(".legend text")
			.filter((d, i) => d === cluster)
			.style("text-decoration", "underline")
	}

	removeHoverCluster(cluster) {
		this.plotData.selectAll(".complex")
			.filter((d, i) => d.cluster !== cluster)
			.style("opacity", 1);
		this.legend.selectAll(".legend text")
			.filter((d, i) => d === cluster)
			.style("text-decoration", "none")
	}
}

window.addEventListener("load", () => {
	new ClusterView(document.getElementById("container"));
});